import { tenantIDAtom } from "atoms/GlobalAtoms";
import LoadingMergeItems from "components/MergeItems/LoadingMergeItems";
import MergeCheckbox from "components/MergeItems/MergeCheckbox";
import SelectedItemInput from "components/MergeItems/SelectedItemInput";
import { useAtomValue } from "jotai";
import { useRef } from "react";
import { useEffect, useMemo } from "react";
import { useState } from "react";
import { Alert, Button, Col, Row } from "reactstrap";
import { Tooltip, Whisper } from "rsuite";
import { newToastEvent } from "utils/ToastUtil";
import AppSpinner from "../../elements/AppSpinner";
import AppDrawer from "./AppDrawer";
import RecordLink from "./Records/MergeRecordsDrawer/RecordLink";
import ViewRecordDrawer from "./Records/ViewRecordDrawer";
const emptyFunc = () => {};

const fieldsToIgnore = [
  "_RecordName",
  "Created",
  "LastUpdated",
  "CreatedBy",
  "LastUpdatedBy",
  "modifiedAt",
];

const initialPayload = {
  ItemToSave: null,
  ItemToDelete: null,
  ItemPayload: {},
};

const MergeItemsDrawer = ({
  open,
  onClose,
  onMerge,
  module,
  itemOne,
  itemTwo,
  loading,
  submitting,
  fieldMap,
  error,
  isSubitemMerge = false,
  hideID = false,
  disableViewRecord = false,
}) => {
  const tenantID = useAtomValue(tenantIDAtom);
  const [viewRecordID, setViewRecordID] = useState(null);
  const [mergePayload, setMergePayload] = useState(initialPayload);
  const [isHovered, setIsHovered] = useState(false);
  const isFirstLoad = useRef(true);
  const itemOneID = itemOne?.id;
  const itemTwoID = itemTwo?.id;

  const oneSelected = mergePayload?.ItemToSave === itemOneID;
  const leftItem = oneSelected ? itemTwo : itemOne;
  const rightItem = oneSelected ? itemOne : itemTwo;
  const leftID = leftItem?.id;
  const rightID = rightItem?.id;

  const itemKeys = useMemo(() => {
    if (!itemOne || !itemTwo || loading) return [];

    const keys1 = Object.keys(itemOne);
    const keys2 = Object.keys(itemTwo);

    const uniqueKeys = keys1.concat(keys2).filter((key, index, array) => {
      if (array.indexOf(key) !== index) return false;
      if (fieldsToIgnore.indexOf(key) !== -1) return false;
      if (key.startsWith("_") || key === "ModuleAPIName") return false;
      if (hideID && (key === "id" || key === "_id")) return false;

      return true;
    });

    return uniqueKeys;
  }, [itemOne, itemTwo, loading, hideID]);

  useEffect(() => {
    if (loading) return;
    if (!isFirstLoad.current) return;

    isFirstLoad.current = false;

    updateSaveDeleteItems(itemOne?.id, itemTwo?.id, true);

    return () => {
      isFirstLoad.current = true;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, itemOne, itemTwo]);

  const isAllSelectedID = isAllSelected();

  const ConfigItemsSection = (
    <>
      <Row className="text-dark align-items-center font-weight-500 mt-2">
        <Col xs="3">Field</Col>
        <Col xs="4" className="d-flex align-items-center">
          <MergeCheckbox
            isSelected={isAllSelectedID === leftID}
            onClick={selectAll(leftID)}
            disabled={isAllSelectedID === leftID}
            css="pl-0 pr-1 text-dark"
          />
          {isSubitemMerge ? (
            <>1st Selected Subitem</>
          ) : (
            <RecordLink
              record={leftItem}
              tenantID={tenantID}
              onOpen={
                disableViewRecord ? emptyFunc : () => setViewRecordID(leftID)
              }
            />
          )}
        </Col>
        <Col
          xs="1"
          className="text-center"
          onClick={() =>
            updateSaveDeleteItems(
              oneSelected ? itemTwoID : itemOneID,
              oneSelected ? itemOneID : itemTwoID
            )
          }
        >
          <Whisper
            speaker={<Tooltip>Swap records to change the merge order</Tooltip>}
            placement="auto"
            trigger="hover"
            delayOpen={300}
            onOpen={() => setIsHovered(true)}
            onClose={() => setIsHovered(false)}
          >
            <Button color="secondary" size="sm" className="btn-icon-only">
              <i
                className={`mdi ${
                  isHovered
                    ? "mdi-swap-horizontal-bold"
                    : "mdi-arrow-right-bold"
                }`}
              />
            </Button>
          </Whisper>
        </Col>
        <Col xs="4" className="d-flex align-items-center">
          <MergeCheckbox
            isSelected={isAllSelectedID === rightID}
            onClick={selectAll(rightID)}
            disabled={isAllSelectedID === rightID}
            css="pl-0 pr-1 text-dark"
          />
          {isSubitemMerge ? (
            <>2nd Selected Subitem</>
          ) : (
            <RecordLink
              record={rightItem}
              tenantID={tenantID}
              onOpen={
                disableViewRecord ? emptyFunc : () => setViewRecordID(leftID)
              }
            />
          )}
        </Col>
      </Row>
      <hr className="my-1" style={{ backgroundColor: "var(--superlight)" }} />
      {itemKeys?.map((key) => {
        const field = fieldMap[key];
        const selectedID = mergePayload.ItemPayload?.[key];
        const noneSelected = !selectedID;
        const leftSelected = selectedID === leftID;
        const rightSelected = selectedID === rightID;
        const disabled = field?.APIName === "id";

        return (
          <Row
            className="my-1 rounded"
            style={{
              backgroundColor: disabled ? "var(--superlight)" : undefined,
            }}
            key={key}
          >
            <Col xs="3" className="d-flex align-items-center">
              {field?.Name}
            </Col>
            <Col xs="4">
              <SelectedItemInput
                field={field}
                item={leftItem}
                moduleAPIName={module?.APIName}
                value={leftItem?.[key]}
                isSelected={leftSelected}
                hideColor={noneSelected || disabled}
                onClick={handleFieldSelection(leftID, key)}
                disabled={disabled}
              />
            </Col>
            <Col xs="1" className="text-center">
              {leftSelected && (
                <i
                  className={`mdi mdi-arrow-right-bold text-medium`}
                  style={{
                    position: "absolute",
                    top: 10,
                    left: 25,

                    fontSize: 20,
                  }}
                />
              )}
            </Col>
            <Col xs="4">
              <SelectedItemInput
                field={field}
                item={rightItem}
                moduleAPIName={module?.APIName}
                value={rightItem?.[key]}
                isSelected={rightSelected}
                hideColor={noneSelected || disabled}
                onClick={handleFieldSelection(rightID, key)}
                disabled={disabled}
              />
            </Col>
          </Row>
        );
      })}
    </>
  );

  return (
    <>
      <AppDrawer
        open={open}
        onClose={handleClose}
        headerTitle="Merging Records"
        size="md"
        error={error}
        headerOptions={
          <Button
            color="primary"
            size="sm"
            style={{ width: 100 }}
            disabled={submitting || !mergePayload?.ItemToSave}
            onClick={handleMerge}
          >
            {submitting ? <AppSpinner size="sm" color="white" /> : "Merge"}
          </Button>
        }
        bodyCSS="pt-3 pb-6"
        body={
          loading ? (
            <LoadingMergeItems />
          ) : (
            <>
              <h4 className="mb-0">Select Record Fields</h4>
              <small>
                Select the fields you want from each record to be merged into
                the final record.
              </small>
              <Alert
                className="py-1 mt-1 px-3 font-weight-500 d-flex align-items-center"
                color="warning"
              >
                <i className="mdi mdi-information text-lg mr-2" />
                Click the{" "}
                <Button
                  color="secondary"
                  size="sm"
                  className="mx-2 btn-icon-only"
                >
                  <i className="mdi mdi-arrow-right-bold" />
                </Button>{" "}
                Button to swap Records
              </Alert>
              {mergePayload?.ItemToSave ? (
                ConfigItemsSection
              ) : (
                <div className="border mt-3 py-2 px-4 text-dark rounded bg-superlight">
                  Select a record to merge before continuing.
                </div>
              )}
            </>
          )
        }
      />
      <ViewRecordDrawer
        open={!!viewRecordID}
        module={module}
        recordID={viewRecordID}
        onClose={() => setViewRecordID(null)}
      />
    </>
  );

  function handleClose() {
    setMergePayload(initialPayload);
    onClose();
  }

  function handleSuccess() {
    newToastEvent("success", "Successfully merged items!");
    handleClose();
  }

  function handleMerge() {
    onMerge({
      moduleAPIName: module?.APIName,
      payload: mergePayload,
      onSuccess: handleSuccess,
    });
  }

  function autoFillItemPayload(itemID, useFallback = true) {
    const item = itemID === itemOne?.id ? itemOne : itemTwo;
    const fallbackItem = itemID === itemOne?.id ? itemTwo : itemOne;

    const newPayload = {};

    for (const key of itemKeys) {
      if (!useFallback) {
        newPayload[key] = itemID;
        continue;
      }

      const itemHasData = Boolean(typeof item[key] === "boolean" || item[key]);
      const fallbackHasData = Boolean(
        typeof item[key] === "boolean" || fallbackItem[key]
      );

      const id = itemHasData
        ? itemID
        : fallbackHasData
        ? fallbackItem?.id
        : itemID;

      newPayload[key] = id;
    }

    return newPayload;
  }

  function updateSaveDeleteItems(saveID, deleteID, isLoad = false) {
    setMergePayload((prev) => {
      let payload;

      if (isLoad) {
        payload = autoFillItemPayload(saveID);
      } else {
        payload = prev.ItemPayload;
      }

      return {
        ItemToSave: saveID,
        ItemToDelete: deleteID,
        ItemPayload: {
          ...payload,
          id: saveID,
        },
      };
    });
  }

  function handleFieldSelection(itemID, fieldAPIName) {
    return () => {
      setMergePayload((prev) => ({
        ...prev,
        ItemPayload: {
          ...prev.ItemPayload,
          [fieldAPIName]: itemID,
        },
      }));
    };
  }

  function selectAll(itemID) {
    return () => {
      const payload = autoFillItemPayload(itemID, false);
      payload.id = rightID;

      setMergePayload((prev) => ({ ...prev, ItemPayload: payload }));
    };
  }

  function isAllSelected() {
    const payload = { ...mergePayload.ItemPayload };
    delete payload.id;

    const itemIds = Object.values(payload);

    const idSet = new Set(itemIds);

    if (idSet.size === 1) {
      return itemIds[0];
    }

    return false;
  }
};

export default MergeItemsDrawer;
